import env from 'Env';
import imgixConfig from 'Config/imgix';


/**
 * Shared imgix / image-URL logic used by the image components
 * (FluidImg, FixedImg and BackgroundImg).
 *
 * Everything here is a small pure function so each component can compose just the
 * pieces it needs. There is no rendering here — only URL and width maths.
 */

/** The fixed ladder of widths offered in responsive srcSets (largest → smallest). */
export const SRCSET_WIDTHS = [1920, 1280, 960, 720, 600, 480, 360, 240, 150];

/**
 * Widths that a measured or `sizes` width is rounded up to (smallest → largest). Each width of
 * each image is a new, billable transformation the first time it is requested, so widths are
 * kept to this fixed set rather than the exact px an element happens to render at.
 */
const SNAP_WIDTHS = [...SRCSET_WIDTHS].reverse().concat([2560, 3840]);

/**
 * Round a width up to the nearest snap width, or down to the largest one.
 *
 * @param {number} width
 * @returns {number}
 */
export const snapWidth = width =>
  SNAP_WIDTHS.find(rung => rung >= width) || SNAP_WIDTHS[SNAP_WIDTHS.length - 1];


// --- source resolution ------------------------------------------------------

/** True for SVG URLs, which are vector and must never be resized by imgix. */
export const isSvg = src => /\.svg(\?|$)/.test(src);

/** Product images live under an `original` subfolder unless told otherwise. */
const resolveSubfolder = (folder, subfolder) =>
  (folder === 'product' && !subfolder ? 'original' : subfolder);

/**
 * Resolve an image to an absolute URL, without any query string.
 *
 * An already-absolute `src` (http/https) is used as-is. Otherwise the URL is the
 * CDN root joined with whatever path segments are given — `src` may itself be a
 * full path from the root (e.g. "assets/banners/x.jpg"), or `folder`/`subfolder`
 * may be supplied and prepended. SVGs are served from the source CDN untouched.
 *
 * @param {Object} params
 * @param {string} params.src         - Absolute URL, or path/file name from the CDN root.
 * @param {string} [params.folder]    - Optional CDN folder to prepend (e.g. 'product').
 * @param {string} [params.subfolder] - Optional subfolder to prepend.
 * @returns {string} The absolute URL, without any imgix query string.
 */
export const resolveImageUrl = ({ src, folder, subfolder }) => {
  if (/^https?:/.test(src)) return src.split('?')[0];

  const root = isSvg(src) ? (env.srcCdnUrl || env.cdnUrl) : env.cdnUrl;
  const sub = resolveSubfolder(folder, subfolder);
  return [root, folder, sub, src].filter(Boolean).join('/');
};


// --- imgix URLs -------------------------------------------------------------

/**
 * Append imgix parameters to a base URL. Pass only the options you need; the
 * parameters are always emitted in the same canonical order.
 *
 * @param {string} base - Absolute image URL, without a query string.
 * @param {Object} [options]
 * @param {number} [options.width]     - Target width in px (`w`).
 * @param {number} [options.height]    - Target height in px (`h`).
 * @param {string} [options.fit='max'] - imgix fit mode (e.g. 'max', 'crop', 'fillmax', 'clip').
 * @param {number} [options.dpr]       - Device pixel ratio; only emitted when > 1.
 * @param {number} [options.quality]   - JPEG/WebP quality (`q`), e.g. 95.
 * @param {string} [options.rect]      - Source crop before resizing, as 'x,y,width,height'.
 * @returns {string} The full imgix URL.
 */
export const buildImgixUrl = (base, {
  width,
  height,
  fit = 'max',
  dpr,
  quality,
  rect,
} = {}) => {
  let url = `${base}?${imgixConfig}`;
  if (rect) url += `&rect=${rect}`;
  if (dpr && dpr > 1) url += `&dpr=${dpr}`;
  if (width) url += `&w=${width}`;
  url += `&fit=${fit}`;
  if (height) url += `&h=${height}`;
  if (quality) url += `&q=${quality}`;
  return url;
};

/**
 * A fixed-width image's srcset, one source per device pixel ratio, and its fallback src (FixedImg). Also used by
 * skeletons, so both request the same URLs.
 * @param {string} url   - Absolute image URL.
 * @param {number} width - The largest CSS px width it renders at.
 * @returns {{ srcSet: string, src: string }}
 */
export const fixedSources = (url, width) => {
  const atDpr = dpr => buildImgixUrl(url, { width, fit: 'fillmax', dpr });
  return {
    srcSet: [1, 2, 3].map(dpr => `${atDpr(dpr)} ${dpr}x`).join(', '),
    src: atDpr(1),
  };
};

// --- width helpers ----------------------------------------------------------

/**
 * The widths to offer in a srcSet: the fixed ladder plus an optional extra width
 * (e.g. an element's measured width, snapped up to a snap width), de-duplicated and
 * ordered largest-first.
 *
 * @param {number} [extraWidth=0] - An additional candidate width; 0 is ignored.
 * @returns {number[]}
 */
export const candidateWidths = (extraWidth = 0) =>
  [...new Set([...SRCSET_WIDTHS, extraWidth > 0 ? snapWidth(extraWidth) : 0])]
    .filter(width => width > 0)
    .sort((a, b) => b - a);

/**
 * Choose the plain-`src` fallback width: the largest candidate still at/below
 * `maxWidth`, or the smallest available if every candidate is larger.
 *
 * @param {number[]} widths   - Candidate widths, largest-first.
 * @param {number}   maxWidth - Upper bound for the fallback.
 * @returns {number}
 */
export const pickFallbackWidth = (widths, maxWidth) =>
  widths.find(width => width <= maxWidth) || widths[widths.length - 1];
